// frontend/src/utils/gridSelection.js
//
// Reading a selection out of a server-side (SSRM) grid.
//
// An SSRM header checkbox is a selection RULE
// (`{selectAll: true, toggledNodes: [...]}`), because the rows it covers were
// never loaded. So `api.getSelectedRows()` is empty after a select-all, and
// `getServerSideSelectionState` is the read. Code that reads
// `getSelectedRows()` sees an empty selection and silently disables the
// action the merchant just asked for.
//
// `readGridSelection` returns the two things a caller actually needs — how
// many rows are selected, and the body fragment that names them to a bulk
// endpoint — and keeps `toggledNodes` to itself. The count is for the button
// label; the payload goes to the server, which resolves the rule against the
// same filter the grid listed with (see BulkOperationMixin.get_selection_queryset).
//
// Sending the RULE rather than a materialized id list makes a large delete one
// request instead of a chunked loop that can half-apply.

/** No rows selected. A stable object so callers can compare identity. */
export const NO_SELECTION = Object.freeze({ count: 0, payload: null });

// What each grid had ticked before its latest selection event. A header click
// replaces the selection with a select-all rule, so what came before can only
// be read here, kept from the event before it.
const ticksBeforeHeaderClick = new WeakMap();

/**
 * Make the header checkbox of a server-side grid act on the CURRENT PAGE, for
 * grids whose bulk action cannot be expressed as a rule. Call it first in
 * every `onSelectionChanged`.
 *
 * `rowSelection.selectAll: 'currentPage'` is documented as INVALID under the
 * server-side row model — AG Grid v36 "will behave as though selectAll =
 * 'all'" — so a grid cannot ask for page-scoped select-all declaratively.
 * These grids need it anyway: their actions post a MATERIALISED row list, so
 * rows the grid never loaded cannot be acted on, and an "all" spanning
 * unloaded pages is a promise the payload cannot keep.
 *
 * A header click toggles the page: when every selectable row on it was
 * already ticked it unticks them, otherwise it ticks them. Rows ticked on
 * other pages stay ticked either way. The toggle cannot be left to AG Grid:
 * an explicit selection shows the header box as indeterminate, and a click on
 * an indeterminate box always reads as "select all".
 *
 * The earlier ticks are kept twice. As the NODES `getSelectedNodes()`
 * returned, because `getSelectedRows()` reads only nodes handed to
 * `setNodesSelected`, so restoring them as nodes gives the caller back exactly
 * the rows it had. And as the selection state's ids, because a row ticked by
 * `setServerSideSelectionState` before it ever loaded has an id there and no
 * node — dropping it would untick a row the merchant never touched.
 *
 * **Synchronous.** After this returns, `api.getSelectedRows()` already
 * reflects the new selection, so callers read the count straight afterwards
 * rather than waiting for a selection event. Any selection event this raises
 * reaches it with `selectAll` false, so it only records the selection.
 *
 * Unselectable rows are passed through rather than filtered: AG Grid refuses
 * them itself — `isRowSelectable` rows "cannot be selected at all, whether
 * using the UI or the API" — and the toggle counts what the grid actually
 * ticked, so they never decide it.
 *
 * @param {object} api  AG Grid api of a server-side grid with pagination.
 */
export function scopeHeaderCheckboxToPage(api) {
  const state = api.getServerSideSelectionState();
  if (!state?.selectAll) {
    ticksBeforeHeaderClick.set(api, {
      nodes: api.getSelectedNodes(),
      ids: state?.toggledNodes ?? [],
    });
    return;
  }

  // Absent when the header click is the grid's first selection event.
  const kept = ticksBeforeHeaderClick.get(api) ?? { nodes: [], ids: [] };

  const pageSize = api.paginationGetPageSize();
  const start = api.paginationGetCurrentPage() * pageSize;
  const page = [];
  for (let i = start; i < start + pageSize; i += 1) {
    const node = api.getDisplayedRowAtIndex(i);
    if (!node) break;  // Past the last row — a short final page.
    page.push(node);
  }

  // deselectAll first: without it the select-all RULE survives and the
  // explicit nodes are added on top of "everything".
  api.deselectAll();
  // Nodes before ids: each step raises a selection event the caller reads,
  // and an id restored ahead of its node would name a row the caller cannot
  // read back yet.
  api.setNodesSelected({ nodes: kept.nodes, newValue: true, source: 'api' });
  if (kept.ids.length) {
    api.setServerSideSelectionState({ selectAll: false, toggledNodes: kept.ids });
  }
  const tickedCount = api.getServerSideSelectionState().toggledNodes.length;
  api.setNodesSelected({ nodes: page, newValue: true, source: 'api' });
  // Nothing new ticked: the page was already fully ticked, so the click unticks it.
  if (api.getServerSideSelectionState().toggledNodes.length === tickedCount) {
    api.setNodesSelected({ nodes: page, newValue: false, source: 'api' });
  }
}

/**
 * The current selection of an SSRM grid.
 *
 * @param {object} api          AG Grid api of a SERVER-SIDE grid. Not
 *   optional and not duck-typed: every caller has one (the selection event
 *   carries it), and `getServerSideSelectionState` ships with the SSRM API
 *   module. Guarding either would turn "this grid is misconfigured" into
 *   "nothing is selected" — silently disabling the action.
 * @param {number} filteredRowCount  Total rows matching the grid's CURRENT
 *   filter — i.e. the `count` from the last datasource response, not the
 *   store's total. Select-all covers the filtered set, so this is what the
 *   merchant is agreeing to when they tick the header box. Throws on a
 *   select-all when it is not a whole number.
 * @returns {{count: number, payload: object|null}} `payload` is null when
 *   nothing is selected, otherwise the body fragment for a bulk endpoint:
 *   `{ids}` for individually ticked rows, or
 *   `{select_all: true, exclude_ids}` for the header checkbox.
 */
export function readGridSelection(api, filteredRowCount) {
  // `null` is a DOCUMENTED return of this API (v36: returns
  // `IServerSideSelectionState | IServerSideGroupSelectionState | null`),
  // meaning no selection state exists yet. That is genuinely "nothing
  // selected", not a swallowed failure.
  const state = api.getServerSideSelectionState();
  if (!state) return NO_SELECTION;

  const toggled = state.toggledNodes;

  if (!state.selectAll) {
    // Rows ticked one by one. `toggledNodes` holds the row ids, which are
    // whatever `getRowId` returned — strings. The backend's BulkIndex
    // normalizes both sides, so they go over the wire as-is.
    return toggled.length
      ? { count: toggled.length, payload: { ids: toggled } }
      : NO_SELECTION;
  }

  // Header checkbox: everything matching the filter, minus anything the
  // merchant then unticked. A count that is not a whole number (a response
  // without one, passed through) would make the arithmetic NaN and read as
  // "nothing selected" — the silent disable this module exists to prevent.
  if (!Number.isInteger(filteredRowCount)) {
    throw new Error(
      `readGridSelection: select-all needs the filtered row count, got ${filteredRowCount}`,
    );
  }
  const count = Math.max(0, filteredRowCount - toggled.length);
  return count
    ? { count, payload: { select_all: true, exclude_ids: toggled } }
    : NO_SELECTION;
}
